import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Patterns dangereux à bloquer (injections SQL et XSS)
 */
const DANGEROUS_PATTERNS = {
  // Patterns SQL Injection
  sqlKeywords: /\b(SELECT|INSERT|UPDATE|DELETE|DROP|TRUNCATE|ALTER|CREATE|EXEC|EXECUTE|UNION|FROM|WHERE|OR|AND|HAVING|ORDER BY|GROUP BY|INTO|VALUES)\b/gi,
  sqlComments: /(--|\/\*|\*\/|#)/g,
  sqlQuotes: /('|"|`)\s*(OR|AND)\s*('|"|`)/gi,
  sqlOperators: /(\bOR\b\s*\d+\s*=\s*\d+|\bAND\b\s*\d+\s*=\s*\d+|1\s*=\s*1|'1'\s*=\s*'1')/gi,

  // Patterns XSS (Cross-Site Scripting)
  scriptTags: /<\s*script[^>]*>.*?<\s*\/\s*script\s*>|<\s*script[^>]*>/gi,
  eventHandlers: /\bon\w+\s*=\s*["'][^"']*["']/gi,
  javascriptUrl: /javascript\s*:/gi,
  htmlTags: /<[^>]*>/g,
  encodedChars: /(%3C|%3E|%22|%27|%3D|&#x3C;|&#x3E;|&#60;|&#62;)/gi,

  // Caractères suspects
  suspiciousChars: /[<>{}|\\^~\[\]`]/g
};

/**
 * Liste des mots-clés SQL dangereux
 */
const SQL_KEYWORDS = [
  'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'DROP', 'TRUNCATE', 'ALTER', 'CREATE',
  'EXEC', 'EXECUTE', 'UNION', 'FROM', 'WHERE', 'HAVING', 'ORDER BY', 'GROUP BY',
  'INTO', 'VALUES', 'TABLE', 'DATABASE', 'SCHEMA', 'GRANT', 'REVOKE', 'PROCEDURE',
  'FUNCTION', 'TRIGGER', 'INDEX', 'VIEW', 'CURSOR', 'FETCH', 'DECLARE', 'SET',
  'WAITFOR', 'DELAY', 'SHUTDOWN', 'XP_', 'SP_', 'INFORMATION_SCHEMA', 'SYSOBJECTS'
];

/**
 * Vérifie si une chaîne contient des patterns SQL dangereux
 */
export function containsSqlInjection(value: string): boolean {
  if (!value || typeof value !== 'string') return false;

  const upperValue = value.toUpperCase();

  // Vérifier les mots-clés SQL
  for (const keyword of SQL_KEYWORDS) {
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    if (regex.test(value)) {
      return true;
    }
  }

  // Vérifier les commentaires SQL
  if (DANGEROUS_PATTERNS.sqlComments.test(value)) {
    return true;
  }

  // Vérifier les opérateurs SQL suspects
  if (DANGEROUS_PATTERNS.sqlOperators.test(value)) {
    return true;
  }

  // Vérifier les quotes suspectes (ex: ' OR '1'='1)
  if (DANGEROUS_PATTERNS.sqlQuotes.test(value)) {
    return true;
  }

  return false;
}

/**
 * Vérifie si une chaîne contient des patterns XSS dangereux
 */
export function containsXss(value: string): boolean {
  if (!value || typeof value !== 'string') return false;

  // Vérifier les balises script
  if (DANGEROUS_PATTERNS.scriptTags.test(value)) {
    return true;
  }

  // Vérifier les gestionnaires d'événements (onclick, onload, etc.)
  if (DANGEROUS_PATTERNS.eventHandlers.test(value)) {
    return true;
  }

  // Vérifier les URLs javascript:
  if (DANGEROUS_PATTERNS.javascriptUrl.test(value)) {
    return true;
  }

  // Vérifier les caractères encodés suspects
  if (DANGEROUS_PATTERNS.encodedChars.test(value)) {
    return true;
  }

  // Vérifier les balises HTML
  if (DANGEROUS_PATTERNS.htmlTags.test(value)) {
    return true;
  }

  return false;
}

/**
 * Vérifie si une chaîne contient des caractères suspects
 */
export function containsSuspiciousChars(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  return DANGEROUS_PATTERNS.suspiciousChars.test(value);
}

/**
 * Validateur Angular pour détecter les injections SQL
 */
export function noSqlInjectionValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    if (containsSqlInjection(control.value)) {
      return {
        sqlInjection: {
          message: 'Le texte contient des caractères ou mots-clés non autorisés (SQL).'
        }
      };
    }

    return null;
  };
}

/**
 * Validateur Angular pour détecter les attaques XSS
 */
export function noXssValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    if (containsXss(control.value)) {
      return {
        xssAttack: {
          message: 'Le texte contient des balises ou scripts non autorisés.'
        }
      };
    }

    return null;
  };
}

/**
 * Validateur combiné pour SQL Injection et XSS
 */
export function secureInputValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const value = control.value;

    if (containsSqlInjection(value)) {
      return {
        securityViolation: {
          type: 'sql',
          message: 'Caractères ou mots-clés SQL non autorisés détectés.'
        }
      };
    }

    if (containsXss(value)) {
      return {
        securityViolation: {
          type: 'xss',
          message: 'Balises ou scripts non autorisés détectés.'
        }
      };
    }

    return null;
  };
}

/**
 * Nettoie une chaîne en supprimant les caractères dangereux
 */
export function sanitizeInput(value: string): string {
  if (!value || typeof value !== 'string') return value;

  let sanitized = value;

  // Supprimer les balises HTML
  sanitized = sanitized.replace(/<[^>]*>/g, '');

  // Supprimer les commentaires SQL
  sanitized = sanitized.replace(/(--|\/\*|\*\/|#)/g, '');

  // Échapper les caractères spéciaux HTML
  sanitized = sanitized
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

  return sanitized;
}

/**
 * Pattern HTML5 sécurisé pour les champs texte
 * Autorise uniquement: lettres, chiffres, espaces, et ponctuation basique
 */
export const SECURE_TEXT_PATTERN = "^[a-zA-ZÀ-ÿ0-9\\s.,;:!?'\"()\\-_@#%&*+=\\/€$£¥]+$";

/**
 * Pattern HTML5 pour les noms (lettres et espaces uniquement)
 */
export const SECURE_NAME_PATTERN = "^[a-zA-ZÀ-ÿ\\s\\-']+$";

/**
 * Pattern HTML5 pour les numéros (chiffres, espaces, tirets, +)
 */
export const SECURE_PHONE_PATTERN = "^[0-9\\s\\-+()]+$";

/**
 * Pattern HTML5 pour les emails
 */
export const SECURE_EMAIL_PATTERN = "^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$";
